import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SubdomainInput } from "./SubdomainInput";
import { registerSelfHostedInstance } from "../../lib/api/cloud";
import type { SelfHostedMode } from "../../types/cloud";

interface RegisterSelfHostedDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function RegisterSelfHostedDialog({ open, onClose, onSuccess }: RegisterSelfHostedDialogProps) {
  const { t } = useTranslation();
  const [subdomain, setSubdomain] = useState("");
  const [valid, setValid] = useState(false);
  const [mode, setMode] = useState<SelfHostedMode | null>(null);
  const [targetIp, setTargetIp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setSubdomain("");
    setValid(false);
    setMode(null);
    setTargetIp("");
    setSubmitting(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!valid || !mode) return;
    if (mode === "public-ip" && !targetIp) return;

    setSubmitting(true);
    setError(null);

    try {
      await registerSelfHostedInstance({
        subdomain,
        mode,
        targetIp: mode === "public-ip" ? targetIp : undefined,
      });
      reset();
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.selfHosted.registerFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const isValidIp = (ip: string) => {
    if (!ip) return false;
    const parts = ip.split(".");
    if (parts.length !== 4) return false;
    return parts.every((p) => {
      const n = Number(p);
      return /^\d{1,3}$/.test(p) && n >= 0 && n <= 255;
    });
  };

  const canSubmit = valid && mode !== null && !submitting && (
    mode === "tunneled" || (mode === "public-ip" && isValidIp(targetIp))
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-[#f5f5f5] mb-1">
          {t('dashboard.selfHosted.registerTitle')}
        </h3>
        <p className="text-[#999] text-sm mb-5">
          {t('dashboard.selfHosted.registerSubtitle')}
        </p>

        {/* Step 1: Subdomain */}
        <div className="mb-5">
          <SubdomainInput value={subdomain} onChange={setSubdomain} onValidation={setValid} />
        </div>

        {/* Step 2: Mode selector */}
        {valid && (
          <div className="mb-5">
            <label className="block text-[#f5f5f5] text-sm font-medium mb-2">
              {t('dashboard.selfHosted.selectMode')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setMode("public-ip")}
                className={`p-4 border rounded-lg text-left transition-colors ${
                  mode === "public-ip"
                    ? "border-blue-500/50 bg-blue-500/5"
                    : "border-[#333] bg-[#111] hover:border-[#555]"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-4 h-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                  <span className="text-[#f5f5f5] text-sm font-medium">
                    {t('dashboard.selfHosted.mode.publicIp')}
                  </span>
                </div>
                <p className="text-[#666] text-xs">
                  {t('dashboard.selfHosted.mode.publicIpDesc')}
                </p>
              </button>
              <button
                type="button"
                onClick={() => setMode("tunneled")}
                className={`p-4 border rounded-lg text-left transition-colors ${
                  mode === "tunneled"
                    ? "border-purple-500/50 bg-purple-500/5"
                    : "border-[#333] bg-[#111] hover:border-[#555]"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-[#f5f5f5] text-sm font-medium">
                    {t('dashboard.selfHosted.mode.tunneled')}
                  </span>
                </div>
                <p className="text-[#666] text-xs">
                  {t('dashboard.selfHosted.mode.tunneledDesc')}
                </p>
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Mode-specific fields */}
        {valid && mode === "public-ip" && (
          <div className="mb-5">
            <label className="block text-[#f5f5f5] text-sm font-medium mb-1.5">
              {t('dashboard.selfHosted.targetIpLabel')}
            </label>
            <input
              type="text"
              value={targetIp}
              onChange={(e) => setTargetIp(e.target.value)}
              placeholder="e.g. 203.0.113.50"
              className="w-full px-3 py-2.5 bg-[#111] border border-[#333] rounded-lg text-[#f5f5f5] text-sm
                         placeholder-[#555] focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500
                         font-mono"
            />
            <p className="text-[#666] text-xs mt-1.5">
              {t('dashboard.selfHosted.targetIpHint')}
            </p>
          </div>
        )}

        {valid && mode === "tunneled" && (
          <div className="mb-5 p-3 bg-purple-500/5 border border-purple-500/20 rounded-lg">
            <p className="text-[#999] text-sm">
              {t('dashboard.selfHosted.tunnelInfo')}
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-500/5 border border-red-500/20 rounded text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-3 border-t border-[#333]">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-[#999] hover:text-[#f5f5f5] transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-400 hover:to-purple-500 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? t('dashboard.selfHosted.registering') : t('dashboard.selfHosted.register')}
          </button>
        </div>
      </div>
    </div>
  );
}
